import React, { createContext, useContext, useState, useEffect } from 'react';
import { BACKEND_URLS, DEFAULT_BASE_URL, setBaseUrl } from '../Utils/APIConstants';

const DebugUrlContext = createContext();

export const useDebugUrl = () => {
  const context = useContext(DebugUrlContext);
  if (!context) {
    throw new Error('useDebugUrl must be used within a DebugUrlProvider');
  }
  return context;
};

export const DebugUrlProvider = ({ children }) => {
  const [currentUrl, setCurrentUrl] = useState(DEFAULT_BASE_URL);
  const [isDebugMode, setIsDebugMode] = useState(false);

  // Initialize from localStorage if available
  useEffect(() => {
    const savedUrl = localStorage.getItem('debug_base_url');
    const savedDebugMode = localStorage.getItem('debug_mode') === 'true';
    
    if (savedUrl && Object.values(BACKEND_URLS).includes(savedUrl)) {
      setCurrentUrl(savedUrl);
      setBaseUrl(savedUrl);
    }
    
    setIsDebugMode(savedDebugMode);
  }, []);

  // If debug UI is disabled, just render children without debug functionality
  if (process.env.REACT_APP_ENABLE_DEBUG_UI !== 'true') {
    return children;
  }

  const changeUrl = (newUrl) => {
    setCurrentUrl(newUrl);
    setBaseUrl(newUrl);
    localStorage.setItem('debug_base_url', newUrl);
    
    // Show notification
    console.log('🔄 Backend URL switched to:', newUrl);
    
    // Optional: Show toast notification
    if (window.showToast) {
      window.showToast(`Backend switched to: ${getUrlDisplayName(newUrl)}`, 'info');
    }
  };

  const toggleDebugMode = () => {
    const newMode = !isDebugMode;
    setIsDebugMode(newMode);
    localStorage.setItem('debug_mode', newMode.toString());
  };

  const resetToDefault = () => {
    setCurrentUrl(DEFAULT_BASE_URL);
    setBaseUrl(DEFAULT_BASE_URL);
    localStorage.removeItem('debug_base_url');
    console.log('🔄 Backend URL reset to default:', DEFAULT_BASE_URL);
  };

  const getUrlDisplayName = (url) => {
    const entry = Object.entries(BACKEND_URLS).find(([key, value]) => value === url);
    return entry ? entry[0] : 'CUSTOM';
  };

  const getCurrentUrlName = () => getUrlDisplayName(currentUrl);

  const value = {
    currentUrl,
    isDebugMode,
    availableUrls: BACKEND_URLS,
    changeUrl,
    toggleDebugMode,
    resetToDefault,
    getCurrentUrlName,
    getUrlDisplayName
  };

  return (
    <DebugUrlContext.Provider value={value}>
      {children}
    </DebugUrlContext.Provider>
  );
};
